// coachSiteDefaults.js — Phase 113 multi-page site model shared by the
// public renderer (components/coachsite) AND the editor (visual editor).
// -----------------------------------------------------------------------
// The site is section-based (NOT freeform). Every page owns an ordered
// list of `{ id, visible }` entries stored in `landing_page.site_sections`.
// When a coach has never customized the new builder, we AUTO-ASSEMBLE
// defaults from their existing profile/content so the site looks complete
// on day one (owner requirement: “put a lot of this together for them”).
//
// Phase 114 adds the optional Meet the Team page (`team`) fed by
// `landing_page.site_team` (imported from Team Management or added by hand).

import { getSiteLayout } from '@/lib/siteLayouts';

export const SITE_PAGES = [
    { id: 'home', label: 'Home', path: '', always: true },
    { id: 'about', label: 'About', path: 'about' },
    { id: 'programs', label: 'Programs', path: 'programs' },
    { id: 'team', label: 'Meet the Team', path: 'team' },
    { id: 'locations', label: 'Locations', path: 'locations' },
    { id: 'contact', label: 'Contact', path: 'contact' },
];

export const PAGE_BY_ID = SITE_PAGES.reduce((acc, p) => { acc[p.id] = p; return acc; }, {});

// Section catalog — label/description used by the editor rail; the
// renderer maps ids → components. `editor` names which edit form opens
// when the section is clicked (aliases into the existing SectionEditor
// switchboard); `auto` means content is assembled from coach data and
// has an informational editor.
export const SECTION_CATALOG = {
    // -- Home ------------------------------------------------------------
    hero: { label: 'Hero', desc: 'Headline, subheadline, photo + main call-to-action', editor: 'hero' },
    trust_strip: { label: 'Trust strip', desc: 'Certifications, experience and Google badge', editor: 'auto_trust' },
    highlights: { label: 'Why choose us', desc: 'Benefit cards — auto-written, fully editable', editor: 'highlights' },
    programs_teaser: { label: 'Programs preview', desc: 'Top packages with a link to the Programs page', editor: 'packages' },
    testimonials: { label: 'Testimonials', desc: 'Reviews from happy parents', editor: 'testimonials' },
    locations_teaser: { label: 'Locations preview', desc: 'Where you teach, with directions', editor: 'auto_locations' },
    travel_check: { label: 'We come to you', desc: 'Address checker — parents see if their home is on your route (Travel Smart)', editor: 'auto_travel' },
    faq: { label: 'FAQ', desc: 'Frequently asked questions', editor: 'auto_faq' },
    cta_band: { label: 'Final call-to-action', desc: 'Bottom conversion band', editor: 'book_cta' },
    // -- About -----------------------------------------------------------
    about_hero: { label: 'Page intro', desc: 'Title + short intro for this page', editor: 'auto_pagehero' },
    story: { label: 'Coach story', desc: 'Your bio and teaching philosophy', editor: 'about' },
    credentials: { label: 'Credentials', desc: 'Certifications and years of experience', editor: 'auto_trust' },
    gallery: { label: 'Photo gallery', desc: 'Up to 6 photos of you teaching', editor: 'gallery' },
    videos: { label: 'Videos', desc: 'YouTube or Vimeo embeds (up to 2)', editor: 'videos' },
    // -- Programs ----------------------------------------------------------
    programs_hero: { label: 'Page intro', desc: 'Title + short intro for this page', editor: 'auto_pagehero' },
    packages: { label: 'Packages & pricing', desc: 'Lesson packages parents can enroll in', editor: 'packages' },
    // -- Team (Phase 114) ----------------------------------------------------
    team_hero: { label: 'Page intro', desc: 'Title + short intro for this page', editor: 'auto_pagehero' },
    team_grid: { label: 'Team members', desc: 'Photo, role and bio for each coach on your team', editor: 'team_grid' },
    // -- Locations ---------------------------------------------------------
    locations_hero: { label: 'Page intro', desc: 'Title + short intro for this page', editor: 'auto_pagehero' },
    locations_grid: { label: 'All locations', desc: 'Every pool with tap-for-directions', editor: 'auto_locations' },
    // -- Contact -----------------------------------------------------------
    contact_hero: { label: 'Page intro', desc: 'Title + short intro for this page', editor: 'auto_pagehero' },
    contact_card: { label: 'Contact options', desc: 'Call, message and apply buttons', editor: 'contact' },
    // -- Pseudo-sections (edited from the Pages tab, never in a page list) --
    site_nav: { label: 'Menu & footer text', desc: 'Navigation labels and footer wording', editor: 'site_nav' },
    site_announcement: { label: 'Promo pop-up', desc: 'Photo + headline + action button shown on your site and parent portal', editor: 'site_announcement' },
};

const entry = (id, visible = true) => ({ id, visible });

/**
 * Build the default per-page section lists, auto-assembled from what the
 * coach already has. Existing custom add-on sections (Awards, FAQ, Team,
 * Promo, CTA strip) are appended to sensible pages by type.
 */
/**
 * Recommended Home order for the active layout (Phase 114 Layouts), with
 * the coach's custom add-on sections appended so nothing disappears.
 */
export function layoutHomeSections(lp) {
    const layout = getSiteLayout(lp);
    const custom = Array.isArray(lp?.custom_sections) ? lp.custom_sections : [];
    const homeCustom = custom
        .filter((cs) => cs?.id && !['awards', 'team', 'promo_banner'].includes(cs.type))
        .map((cs) => entry(cs.id));
    const order = layout.homeOrder || [];
    const idx = order.indexOf('testimonials');
    const before = order.slice(0, idx >= 0 ? idx + 1 : order.length).map((id) => entry(id));
    const after = order.slice(idx >= 0 ? idx + 1 : order.length).map((id) => entry(id));
    return [...before, ...homeCustom, ...after];
}

export function buildDefaultSiteSections(lp) {
    const custom = Array.isArray(lp?.custom_sections) ? lp.custom_sections : [];
    const customByPage = { home: [], about: [], programs: [], team: [] };
    custom.forEach((cs) => {
        if (!cs?.id) return;
        // NOTE: the legacy "Meet the Team" add-on stays on About (where it
        // has always rendered) — the new Team PAGE is powered by site_team.
        if (cs.type === 'awards' || cs.type === 'team') customByPage.about.push(entry(cs.id));
        else if (cs.type === 'promo_banner') customByPage.programs.push(entry(cs.id));
        else customByPage.home.push(entry(cs.id)); // faq, cta_strip, unknown
    });
    return {
        home: layoutHomeSections(lp),
        about: [
            entry('about_hero'),
            entry('story'),
            entry('credentials'),
            entry('gallery'),
            entry('videos'),
            ...customByPage.about,
            entry('cta_band'),
        ],
        programs: [
            entry('programs_hero'),
            entry('packages'),
            ...customByPage.programs,
            entry('faq'),
            entry('cta_band'),
        ],
        team: [
            entry('team_hero'),
            entry('team_grid'),
            ...customByPage.team,
            entry('cta_band'),
        ],
        locations: [
            entry('locations_hero'),
            entry('travel_check'),
            entry('locations_grid'),
            entry('cta_band'),
        ],
        contact: [
            entry('contact_hero'),
            entry('contact_card'),
            entry('travel_check'),
            entry('locations_grid'),
            entry('cta_band'),
        ],
    };
}

/**
 * Return the (sanitized) section list for a page — saved config first,
 * defaults otherwise. Also appends any NEW custom sections that were
 * added after the coach saved their layout so nothing silently vanishes.
 */
export function getPageSections(lp, pageId) {
    const defaults = buildDefaultSiteSections(lp);
    const saved = lp?.site_sections?.[pageId];
    const base = Array.isArray(saved) && saved.length
        ? saved.filter((s) => s && typeof s.id === 'string')
        : (defaults[pageId] || []);
    // Phase 136 — Travel Smart section shipped after many coaches saved
    // their layouts. Append it where it belongs so it appears without a
    // re-save (it renders nothing publicly until Travel Smart is on).
    if (Array.isArray(saved) && saved.length && ['home', 'locations', 'contact'].includes(pageId)) {
        const everywhere = new Set();
        SITE_PAGES.forEach((p) => (Array.isArray(lp?.site_sections?.[p.id]) ? lp.site_sections[p.id] : []).forEach((s) => s?.id && everywhere.add(s.id)));
        if (!everywhere.has('travel_check')) {
            const anchorIdx = base.findIndex((s) => s.id === (pageId === 'contact' ? 'contact_card' : pageId === 'home' ? 'locations_teaser' : 'locations_hero'));
            const ins = { id: 'travel_check', visible: true };
            if (anchorIdx >= 0) base.splice(anchorIdx + 1, 0, ins); else base.push(ins);
        }
    }
    // Append custom sections missing from EVERY page (newly added ones).
    if (Array.isArray(lp?.custom_sections) && lp.custom_sections.length) {
        const allSaved = new Set();
        SITE_PAGES.forEach((p) => {
            const list = (Array.isArray(lp?.site_sections?.[p.id]) && lp.site_sections[p.id].length)
                ? lp.site_sections[p.id] : (defaults[p.id] || []);
            list.forEach((s) => s?.id && allSaved.add(s.id));
        });
        if (pageId === 'home') {
            lp.custom_sections.forEach((cs) => {
                if (cs?.id && !allSaved.has(cs.id)) base.push({ id: cs.id, visible: true });
            });
        }
    }
    return base;
}

/** Visible Meet-the-Team members (Phase 114). */
export function visibleTeamMembers(lp) {
    const members = Array.isArray(lp?.site_team?.members) ? lp.site_team.members : [];
    return members.filter((m) => m && (m.name || '').trim() && m.visible !== false);
}

/**
 * Page visibility. Home is always visible. Locations defaults to visible
 * only when the coach actually has locations; Team defaults to visible
 * only when at least one visible member exists. Everything else defaults on.
 */
export function isPageVisible(lp, coach, pageId) {
    if (pageId === 'home') return true;
    const explicit = lp?.site_pages?.[pageId];
    if (typeof explicit === 'boolean') return explicit;
    if (pageId === 'locations') return (coach?.locations || []).length > 0;
    if (pageId === 'team') return visibleTeamMembers(lp).length > 0;
    return true;
}

/** Ordered list of visible pages for the navbar. */
export function visiblePages(lp, coach) {
    return SITE_PAGES.filter((p) => isPageVisible(lp, coach, p.id));
}
